import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from '@react-google-maps/api';
import './map-with-cards.scss';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const mapContainerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  borderRadius: '12px',
};

const defaultCenter = { lat: 51.5074, lng: -0.1278 };

export interface LatLng {
  lat: number;
  lng: number;
}

export interface MapItem {
  id: string;
  title: string;
  address?: string;
  lat?: number;
  lng?: number;
  imageUrl?: string;
  highlighted?: boolean;
  highlightedReason?: string;
  fields: { label: string; value: string }[];
}

export interface PointOfInterest {
  label: string;
  address?: string;
  lat?: number;
  lng?: number;
}

export interface MapWithCardsProps {
  items: MapItem[];
  pointsOfInterest?: PointOfInterest[];
  /** IDs of currently selected items — markers turn blue when selected */
  selectedIds?: string[];
  /** Render prop for each card in the overlay panel */
  renderCard: (
    item: MapItem,
    cardProps: {
      isFocused: boolean;
      onMouseEnter: () => void;
      onMouseLeave: () => void;
      onView: () => void;
      cardRef: (el: HTMLDivElement | null) => void;
    },
  ) => React.ReactNode;
  /** Render prop for detail view panel shown when a card is clicked */
  renderDetailView?: (item: MapItem, onClose: () => void) => React.ReactNode;
  /** Height of the map container (default: 600px) */
  height?: number | string;
  /** Class name applied to the root element */
  className?: string;
}

let _bluePinIcon: google.maps.Symbol | null = null;
function getBluePinIcon(): google.maps.Symbol {
  if (!_bluePinIcon) {
    _bluePinIcon = {
      path: 'M 12,2 C 8.13,2 5,5.13 5,9 c 0,5.25 7,13 7,13 0,0 7,-7.75 7,-13 0,-3.87 -3.13,-7 -7,-7 z',
      fillColor: '#489FD9',
      fillOpacity: 1,
      strokeColor: '#3678A8',
      strokeWeight: 1.5,
      scale: 2,
      anchor: new google.maps.Point(12, 22),
    };
  }
  return _bluePinIcon;
}

const MapWithCards: React.FC<MapWithCardsProps> = ({
  items,
  pointsOfInterest = [],
  selectedIds = [],
  renderCard,
  renderDetailView,
  height = 600,
  className,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const [resolvedPositions, setResolvedPositions] = useState<
    Map<string, LatLng>
  >(new Map());
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  const [focusedCardId, setFocusedCardId] = useState<string | null>(null);
  const [viewingItemId, setViewingItemId] = useState<string | null>(null);
  const [poiPositions, setPoiPositions] = useState<Map<number, LatLng>>(
    new Map(),
  );
  const [activePoiIndex, setActivePoiIndex] = useState<number | null>(null);

  const geocodedIds = useRef<Set<string>>(new Set());
  const poiGeocodedIndices = useRef<Set<number>>(new Set());
  const mapRef = useRef<google.maps.Map | null>(null);
  const cardRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cardsWrapperRef = useRef<HTMLDivElement | null>(null);

  const scrollToCard = useCallback((id: string) => {
    const el = cardRefsMap.current.get(id);
    const container = cardsWrapperRef.current;
    if (el && container) {
      const elTop = el.offsetTop - container.offsetTop;
      const elHeight = el.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTarget = elTop - (containerHeight - elHeight) / 2;
      container.scrollTo({ top: scrollTarget, behavior: 'smooth' });

      setFocusedCardId(id);
      if (focusTimeoutRef.current) clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = setTimeout(() => setFocusedCardId(null), 1500);
    }
  }, []);

  // Geocode item addresses to lat/lng for map markers.
  useEffect(() => {
    if (!isLoaded) return;

    const needsGeocode = items.filter((item) => {
      if (geocodedIds.current.has(item.id)) return false;
      return item.address && item.lat == null && item.lng == null;
    });

    if (needsGeocode.length === 0) return;

    for (const item of needsGeocode) {
      geocodedIds.current.add(item.id);
    }

    const geocoder = new google.maps.Geocoder();
    const batch = new Map<string, LatLng>();

    Promise.all(
      needsGeocode.map(
        (item) =>
          new Promise<void>((resolve) => {
            geocoder.geocode({ address: item.address }, (results, status) => {
              if (
                status === google.maps.GeocoderStatus.OK &&
                results &&
                results[0]
              ) {
                const loc = results[0].geometry.location;
                batch.set(item.id, { lat: loc.lat(), lng: loc.lng() });
              }
              resolve();
            });
          }),
      ),
    ).then(() => {
      if (batch.size === 0) return;
      setResolvedPositions((prev) => {
        const next = new Map(prev);
        batch.forEach((pos, id) => next.set(id, pos));
        return next;
      });
    });
  }, [isLoaded, items]);

  // Geocode POI addresses to lat/lng
  useEffect(() => {
    if (!isLoaded || pointsOfInterest.length === 0) return;

    const needsGeocode = pointsOfInterest
      .map((poi, idx) => ({ poi, idx }))
      .filter(({ poi, idx }) => {
        if (poiGeocodedIndices.current.has(idx)) return false;
        return poi.address && poi.lat == null && poi.lng == null;
      });

    // Resolve POIs with explicit lat/lng immediately
    const explicitBatch = new Map<number, LatLng>();
    pointsOfInterest.forEach((poi, idx) => {
      if (poi.lat != null && poi.lng != null) {
        explicitBatch.set(idx, { lat: poi.lat, lng: poi.lng });
        poiGeocodedIndices.current.add(idx);
      }
    });
    if (explicitBatch.size > 0) {
      setPoiPositions((prev) => {
        const next = new Map(prev);
        explicitBatch.forEach((pos, idx) => next.set(idx, pos));
        return next;
      });
    }

    if (needsGeocode.length === 0) return;

    for (const { idx } of needsGeocode) {
      poiGeocodedIndices.current.add(idx);
    }

    const geocoder = new google.maps.Geocoder();
    const batch = new Map<number, LatLng>();

    Promise.all(
      needsGeocode.map(
        ({ poi, idx }) =>
          new Promise<void>((resolve) => {
            geocoder.geocode({ address: poi.address }, (results, status) => {
              if (
                status === google.maps.GeocoderStatus.OK &&
                results &&
                results[0]
              ) {
                const loc = results[0].geometry.location;
                batch.set(idx, { lat: loc.lat(), lng: loc.lng() });
              }
              resolve();
            });
          }),
      ),
    ).then(() => {
      if (batch.size === 0) return;
      setPoiPositions((prev) => {
        const next = new Map(prev);
        batch.forEach((pos, idx) => next.set(idx, pos));
        return next;
      });
    });
  }, [isLoaded, pointsOfInterest]);

  // Re-fit bounds when positions change
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;

    const allPositions: LatLng[] = [];
    resolvedPositions.forEach((pos) => allPositions.push(pos));
    items.forEach((item) => {
      if (item.lat != null && item.lng != null) {
        allPositions.push({ lat: item.lat, lng: item.lng });
      }
    });
    poiPositions.forEach((pos) => allPositions.push(pos));

    if (allPositions.length === 0) return;

    if (allPositions.length === 1) {
      mapRef.current.setCenter(allPositions[0]);
      mapRef.current.setZoom(14);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    allPositions.forEach((pos) => bounds.extend(pos));
    mapRef.current.fitBounds(bounds, {
      top: 50,
      right: 50,
      bottom: 50,
      left: 280,
    });
  }, [isLoaded, items, resolvedPositions, poiPositions]);

  // Compute initial map center once
  const initialCenterRef = useRef<LatLng | null>(null);
  if (!initialCenterRef.current && items.length > 0) {
    for (const item of items) {
      if (item.lat != null && item.lng != null) {
        initialCenterRef.current = { lat: item.lat, lng: item.lng };
        break;
      }
    }
  }
  if (!initialCenterRef.current && poiPositions.size > 0) {
    const firstPoi = poiPositions.values().next().value;
    if (firstPoi) initialCenterRef.current = firstPoi;
  }
  const initialCenter = initialCenterRef.current || defaultCenter;

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const getPosition = useCallback(
    (item: MapItem): LatLng | null => {
      if (item.lat != null && item.lng != null) {
        return { lat: item.lat, lng: item.lng };
      }
      return resolvedPositions.get(item.id) || null;
    },
    [resolvedPositions],
  );

  const viewingItem = viewingItemId
    ? items.find((i) => i.id === viewingItemId) || null
    : null;

  return (
    <div
      className={`map-with-cards${className ? ` ${className}` : ''}`}
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
    >
      <div className='map-wrapper'>
        {!GOOGLE_MAPS_API_KEY ? (
          <div className='map-placeholder'>
            Google Maps API key not configured.
          </div>
        ) : loadError ? (
          <div className='map-placeholder'>Failed to load Google Maps.</div>
        ) : !isLoaded ? (
          <div className='map-placeholder'>Loading map…</div>
        ) : (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={initialCenter}
            zoom={12}
            onLoad={onMapLoad}
            options={{
              disableDefaultUI: false,
              zoomControl: true,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              styles: [
                {
                  featureType: 'poi',
                  elementType: 'labels',
                  stylers: [{ visibility: 'off' }],
                },
              ],
            }}
          >
            {items.map((item) => {
              const pos = getPosition(item);
              if (!pos) return null;
              return (
                <Marker
                  key={item.id}
                  position={pos}
                  animation={
                    hoveredCardId === item.id
                      ? google.maps.Animation.BOUNCE
                      : undefined
                  }
                  icon={
                    selectedIds.includes(item.id) ? getBluePinIcon() : undefined
                  }
                  onClick={() => scrollToCard(item.id)}
                />
              );
            })}
            {pointsOfInterest.map((poi, idx) => {
              const pos = poiPositions.get(idx);
              if (!pos) return null;
              return (
                <Marker
                  key={`poi-${idx}`}
                  position={pos}
                  icon={{
                    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
                    fillColor: '#4285F4',
                    fillOpacity: 1,
                    strokeColor: '#3367D6',
                    strokeWeight: 1,
                    scale: 1.5,
                    anchor: new google.maps.Point(12, 22),
                  }}
                  onClick={() =>
                    setActivePoiIndex(activePoiIndex === idx ? null : idx)
                  }
                >
                  {activePoiIndex === idx && (
                    <InfoWindow onCloseClick={() => setActivePoiIndex(null)}>
                      <div style={{ fontWeight: 600, fontSize: '13px' }}>
                        {poi.label}
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              );
            })}
          </GoogleMap>
        )}

        <div
          className={`cards-wrapper${items.length === 0 ? ' cards-hidden' : ' cards-visible'}`}
          ref={cardsWrapperRef}
        >
          {items.map((item) =>
            renderCard(item, {
              isFocused: focusedCardId === item.id,
              onMouseEnter: () => setHoveredCardId(item.id),
              onMouseLeave: () => setHoveredCardId(null),
              onView: () => setViewingItemId(item.id),
              cardRef: (el) => {
                if (el) cardRefsMap.current.set(item.id, el);
                else cardRefsMap.current.delete(item.id);
              },
            }),
          )}
        </div>

        {viewingItem &&
          renderDetailView &&
          renderDetailView(viewingItem, () => setViewingItemId(null))}
      </div>
    </div>
  );
};

export default MapWithCards;
